
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useFeedbackRequests } from './useFeedbackRequests';
import FeedbackRequestCard from './FeedbackRequestCard';
import EmptyRequestList from './EmptyRequestList';
import RequestListHeader from './RequestListHeader';
import type { UserRole } from '@/types/feedback';

type FeedbackRequestsListProps = {
  type: 'sent' | 'received';
  context?: UserRole;
  statusFilter?: string;
};

const FeedbackRequestsList = ({ type, context, statusFilter }: FeedbackRequestsListProps) => {
  const { requests, loading, isCandidat, isEntreprise, fetchRequests, navigateToRequestForm } = useFeedbackRequests(type);
  const [filteredRequests, setFilteredRequests] = useState(requests);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (context) {
      sessionStorage.setItem('adminContext', context);
    }
  }, [context]);

  // Appliquer le filtre de statut si nécessaire
  useEffect(() => {
    if (statusFilter) {
      const filtered = requests.filter(request => request.status === statusFilter);
      setFilteredRequests(filtered);
    } else {
      setFilteredRequests(requests);
    }
  }, [requests, statusFilter]);

  // Configurer un rafraîchissement initial et moins fréquent
  useEffect(() => {
    // Prevent double initialization
    if (mountedRef.current) return;
    mountedRef.current = true;
    
    // Charger les données immédiatement au montage du composant
    fetchRequests();
    
    // Configurer un intervalle de rafraîchissement moins fréquent (120 secondes)
    const interval = setInterval(() => {
      console.log(`Scheduled refresh for ${type} requests`);
      fetchRequests();
    }, 120000); // 2 minutes
    
    refreshTimerRef.current = interval;

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [fetchRequests, type]);

  // Nettoyer l'intervalle lorsque le composant est démonté
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  // Fonction pour rafraîchir les données après une action
  const handleActionPerformed = useCallback(() => {
    console.log("Action performed, refreshing data once...");
    fetchRequests();
  }, [fetchRequests]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
      </div>
    );
  }

  // Utiliser les requêtes filtrées ou une liste vide si aucune
  const requestsToShow = filteredRequests.length > 0 ? filteredRequests : [];

  if (requestsToShow.length === 0) {
    return <EmptyRequestList 
      type={type} 
      isCandidat={isCandidat} 
      onRequestFeedback={navigateToRequestForm} 
    />;
  }

  return (
    <Card className="shadow-sm">
      <RequestListHeader type={type} isCandidat={isCandidat} isEntreprise={isEntreprise} />
      <CardContent>
        <div className="space-y-4">
          {requestsToShow.map((request) => (
            <FeedbackRequestCard 
              key={request.id}
              request={request} 
              type={type} 
              onActionPerformed={handleActionPerformed}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default FeedbackRequestsList;
